// Stocky Low Stock CSV の発注点・仕入先別リードタイム取り込み。
// 発注点とリードタイムは Stocky API に無く、Low Stock CSV で移す。
// MOQ は仕入先別の Order limits 専用貼り付け取込だけで扱う。
// ファイルは base64 の JSON ボディで送る（multipart 依存を増やさない）。
import { apiFetch } from "./client";
import {
  normalizeStockyImportArchiveReceipt,
  type StockyImportArchiveReceipt,
} from "./stocky_csv_import";

export interface CsvImportPreview {
  headers: string[];
  sample_rows: string[][];
  total_rows: number;
  /** ヘッダ名からの対応付けの推測（初期値の提案。確定はマーチャント）。 */
  guessed_mapping: Record<string, string | null>;
}

export interface CsvImportMapping {
  sku: string;
  supplier_name?: string | null;
  reorder_point?: string | null;
  lead_time_days?: string | null;
}

export interface CsvImportInvalidRow {
  row: number;
  reason: string;
}

export interface CsvImportResult {
  total_rows: number;
  created: number;
  updated: number;
  unchanged: number;
  matched_variants: number;
  unmatched_skus: string[];
  ambiguous_skus: string[];
  unmatched_suppliers: string[];
  ambiguous_suppliers: string[];
  invalid_rows: CsvImportInvalidRow[];
  /** 🔴 真の件数。上の配列は表示用に切り詰め済み（.length を件数にしない）。旧APIでは欠ける。 */
  invalid_row_count?: number;
  unmatched_count?: number;
  ambiguous_sku_count: number;
  unmatched_supplier_count: number;
  ambiguous_supplier_count: number;
  /** 仕入先別リードタイムの新規リンク・既存リンク上書き件数。 */
  lead_times_created: number;
  lead_times_updated: number;
  lead_times_unchanged: number;
  preferred_repaired: number;
  /** true なら件数を数えただけで DB には書いていない（確定前の確認）。 */
  dry_run: boolean;
  plan_token: string | null;
  normalization_mode: "domain" | "retention_only";
  normalized_rows: number;
  unprocessed_rows: number;
  domain_normalization_complete: boolean;
  retention_reason: "hard_row_cap_exceeded" | null;
  source_archive: StockyImportArchiveReceipt | null;
}

/** File → base64。大きなファイルでも呼び出しスタックを溢れさせないよう分割変換。 */
export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const CHUNK_SIZE = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK_SIZE));
  }
  return btoa(binary);
}

export function previewReorderCsv(fileBase64: string): Promise<CsvImportPreview> {
  return apiFetch<CsvImportPreview>(
    "/api/reorder-policies/low-stock-import/preview",
    {
      method: "POST",
      body: { file_base64: fileBase64 },
    },
  );
}

/**
 * 取り込みを実行する。`dryRun` なら件数だけ数えて DB には書かない
 * （既存の設定を何件上書きするかを確定前に見せるため）。
 */
export async function applyReorderCsv(
  fileBase64: string,
  mapping: CsvImportMapping,
  options: {
    dryRun?: boolean;
    expectedPlanToken?: string;
    confirmRetentionOnly?: boolean;
    sourceImportId: string;
    sourceFileName: string;
    sourceMediaType: string;
  },
): Promise<CsvImportResult> {
  const response = await apiFetch<CsvImportResult>(
    "/api/reorder-policies/low-stock-import/apply",
    {
      method: "POST",
      body: {
        file_base64: fileBase64,
        mapping,
        dry_run: options.dryRun ?? false,
        source_import_id: options.sourceImportId,
        source_file_name: options.sourceFileName,
        source_media_type: options.sourceMediaType,
        ...(options.expectedPlanToken === undefined
          ? {}
          : { expected_plan_token: options.expectedPlanToken }),
        ...(options.confirmRetentionOnly
          ? { confirm_retention_only: true }
          : {}),
      },
    },
  );
  return {
    ...response,
    source_archive:
      response.source_archive == null
        ? null
        : normalizeStockyImportArchiveReceipt(response.source_archive),
  };
}
